import { ChatThread } from '@tunasong/plugin-chat/ui'
import type { Chat, Persisted } from '@tunasong/schemas'
import type { FC } from 'react'
import { useId } from 'react'
import type { CommentedBlock } from './commented-blocks.js'
import { commentedBlockHeading } from './commented-blocks.js'
import styles from './comment-group.module.css'

export interface CommentGroupProps {
  block: CommentedBlock
  chat: Persisted<Chat>
  onShowInSong(): void
}

/** The section tokens (`--tuna-section-*`); a block outside a known section takes `other` */
const sectionTokens = ['song', 'intro', 'verse', 'chorus', 'outro', 'bridge', 'middle8', 'riff']

const sectionToken = (sectionType?: string) => {
  const token = sectionType?.toLowerCase()
  return token && sectionTokens.includes(token) ? token : 'other'
}

/**
 * A block's thread in the sidebar Chat (chat makeover M3, #2718): "On {label}" with the section's colour as an accent
 * only, "Show in song", then its open comments. The group is boxed off, so a song-level comment that lands right
 * under it never reads as part of it (Joni, Gestalt proximity). Replies are written in the block's popup.
 */
export const CommentGroup: FC<CommentGroupProps> = props => {
  const { block, chat, onShowInSong } = props
  const headingId = useId()
  const heading = commentedBlockHeading(block)
  const target = block.quoted ? `“${block.label}”` : block.label

  return (
    <section className={styles.group} aria-labelledby={headingId} data-section={sectionToken(block.sectionType)}>
      <div className={styles.header}>
        <span className={styles.accent} aria-hidden="true" />
        <span id={headingId} className={styles.heading} title={heading}>
          {heading}
        </span>
        <button type="button" className={styles.show} aria-label={`Show in song: ${target}`} onClick={onShowInSong}>
          Show in song
        </button>
      </div>
      <ChatThread element={chat} className={styles.thread} />
    </section>
  )
}
